<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore, useCommunityStore } from '@/stores';

import UserProfileMenu from '@/components/UserProfileMenu.vue';
import LeftSidebar from '@/components/LeftSidebar.vue';
import CommunitySwitcher from '@/components/CommunitySwitcher.vue';
import { getLanguageEnabled, getTranslation } from '@/composables/data-display-utils';
import { formatAccessUntil, notifyCustomerAccessEnded } from '@/helpers/customerAccess.helper';

const DRAWER_COLLAPSED_KEY = 'cocoriko-admin-drawer-collapsed';
const isDrawerCollapsed = ref(false);

const { t } = useI18n();
const authStore = useAuthStore();
const communityStore = useCommunityStore();

const toggleLeftDrawer = () => {
  isDrawerCollapsed.value = !isDrawerCollapsed.value;
  localStorage.setItem(DRAWER_COLLAPSED_KEY, JSON.stringify(isDrawerCollapsed.value));
};

onMounted(() => {
  const customerId = communityStore.endedCustomerAccess;
  if (customerId) {
    communityStore.endedCustomerAccess = null;
    notifyCustomerAccessEnded(communityStore.titleOf(customerId), () =>
      communityStore.switchCommunity({ id: customerId }, true),
    );
  }
  const storedValue = localStorage.getItem(DRAWER_COLLAPSED_KEY);
  if (storedValue !== null) {
    isDrawerCollapsed.value = JSON.parse(storedValue);
  }
});

const languageEnabled = computed(() => getLanguageEnabled(communityStore.community));

// Kept in sight while a superadmin is inside a customer's community, since the
// mistake is forgetting where one is (COCO-3425).
const customerAccessUntil = computed(() =>
  authStore.currentCommunity ? communityStore.customerAccessUntil(authStore.currentCommunity) : null,
);

const communityName = computed(() => {
  if (communityStore.community) {
    return getTranslation(communityStore.community, 'title', languageEnabled.value);
  }
  return '';
});
</script>

<template>
  <q-layout view="hHh LpR fFf">
    <q-header elevated class="bg-primary">
      <q-toolbar>
        <q-avatar>
          <img :alt="$t('community.image.logo.alt-text')" :src="communityStore.community?.logo_file?.url ?? ''" />
        </q-avatar>
        <q-toolbar-title>
          {{ communityName }}
          <q-chip v-if="customerAccessUntil" dense color="warning" text-color="black" icon="lock_open">
            {{ t('customerAccess.chip', { time: formatAccessUntil(customerAccessUntil) }) }}
          </q-chip>
          <q-btn flat round icon="arrow_drop_down">
            <community-switcher />
          </q-btn>
        </q-toolbar-title>
        <user-profile-menu :user-name="authStore.user?.email ?? 'undefined'" />
      </q-toolbar>
    </q-header>

    <q-drawer
      show-if-above
      :mini="isDrawerCollapsed"
      side="left"
      behavior="desktop"
      bordered
      class="bg-primary sidebar"
    >
      <div class="q-mini-drawer absolute" v-if="!isDrawerCollapsed" style="top: 15px; right: -15px">
        <q-btn dense round unelevated size="md" color="secondary" @click="toggleLeftDrawer" class="drawer-toggle">
          <q-icon name="chevron_left" class="cursor-pointer" color="white" />
        </q-btn>
      </div>
      <left-sidebar @toggle="toggleLeftDrawer" :is-drawer-collapsed="isDrawerCollapsed" :role="authStore.role" />
    </q-drawer>

    <q-page-container>
      <router-view />
    </q-page-container>
  </q-layout>
</template>
<style lang="scss">
.q-layout {
  .q-btn {
    &.drawer-toggle {
      z-index: 2;
    }

    &.community-switcher .q-icon {
      color: grey;
    }
  }
  .q-drawer__content.sidebar {
    display: flex;
    flex-direction: column;
  }
}
</style>
